import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, MailIcon, EditIcon, PrinterIcon, CheckCircleIcon, XCircleIcon, MoreHorizontalIcon, ArchiveIcon, TrashIcon } from 'lucide-react';
import { Invoice } from '../types/Invoice';
export function InvoiceDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  // Mock data
  const invoice: Invoice = {
    id: id || '1',
    invoiceNumber: 'INV-2024-001',
    customerName: 'Acme Corporation',
    customerEmail: 'billing@acme.com',
    customerPhone: '+60 12 345 6789',
    amount: 125000,
    currency: 'THB',
    status: 'paid',
    dueDate: '2024-01-15',
    issueDate: '2024-01-01',
    paidDate: '2024-01-10',
    description: 'Monthly subscription fee',
    notes: 'Thank you for your business!',
    items: [{
      id: '1',
      description: 'Premium Plan Subscription',
      quantity: 1,
      unitPrice: 100000,
      total: 100000
    }, {
      id: '2',
      description: 'Additional User Licenses',
      quantity: 5,
      unitPrice: 5000,
      total: 25000
    }],
    subtotal: 125000,
    taxAmount: 0,
    discountAmount: 0,
    total: 125000
  };
  const getStatusBadge = (status: Invoice['status']) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700 border-gray-200',
      sent: 'bg-blue-50 text-blue-700 border-blue-200',
      paid: 'bg-green-50 text-green-700 border-green-200',
      overdue: 'bg-red-50 text-red-700 border-red-200',
      cancelled: 'bg-gray-100 text-gray-500 border-gray-200'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${styles[status]}`} data-id="element-8896">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  return <div className="max-w-[1400px] mx-auto space-y-6" data-id="element-8897">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-8898">
        <div className="flex items-center gap-4" data-id="element-8899">
          <button onClick={() => navigate('/invoices')} className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-8900">
            <ArrowLeftIcon size={18} data-id="element-8901" />
          </button>
          <div data-id="element-8902">
            <div className="flex items-center gap-3" data-id="element-8903">
              <h1 className="text-xl font-semibold text-gray-900" data-id="element-8904">
                {invoice.invoiceNumber}
              </h1>
              {getStatusBadge(invoice.status)}
            </div>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-8905">
              {invoice.customerName}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-8906">
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-8907">
            <MailIcon size={16} data-id="element-8908" />
            Send
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-8909">
            <DownloadIcon size={16} data-id="element-8910" />
            Download
          </button>
          <button onClick={() => navigate(`/invoice-form?id=${invoice.id}`)} className="flex items-center gap-2 px-4 py-2 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-8911">
            <EditIcon size={16} data-id="element-8912" />
            Edit
          </button>
          <div className="relative" data-id="element-8913">
            <button onClick={() => setShowMoreMenu(!showMoreMenu)} className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-8914">
              <MoreHorizontalIcon size={18} data-id="element-8915" />
            </button>
            {showMoreMenu && <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMoreMenu(false)} data-id="element-8916" />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-8917">
                  <button onClick={() => {
                setShowMoreMenu(false);
              }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-8918">
                    <PrinterIcon size={16} className="mr-3 text-gray-400" data-id="element-8919" />
                    Print
                  </button>
                  <button onClick={() => {
                setShowMoreMenu(false);
              }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-8920">
                    <ArchiveIcon size={16} className="mr-3 text-gray-400" data-id="element-8921" />
                    Archive
                  </button>
                  <div className="border-t border-gray-100 my-1" data-id="element-8922" />
                  <button onClick={() => {
                setShowMoreMenu(false);
              }} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-8923">
                    <TrashIcon size={16} className="mr-3" data-id="element-8924" />
                    Delete
                  </button>
                </div>
              </>}
          </div>
        </div>
      </div>

      {/* Invoice Content */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-8925">
        {/* Invoice Header */}
        <div className="p-8" data-id="element-8926">
          <div className="flex justify-between items-start mb-8" data-id="element-8927">
            <div data-id="element-8928">
              <h2 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-8929">INVOICE</h2>
              <p className="text-sm text-gray-500" data-id="element-8930">{invoice.invoiceNumber}</p>
            </div>
            <div className="text-right" data-id="element-8931">
              <img src="https://cdn.magicpatterns.com/uploads/vqr9LriSp5eEW29oygg9EF/rabbit_logo_sidebar.svg" alt="Rabbit Gateway" className="h-10 mb-3 object-contain ml-auto" data-id="element-8932" />
              <p className="text-sm text-gray-600" data-id="element-8933">Rabbit Gateway</p>
              <p className="text-xs text-gray-500" data-id="element-8934">Bangkok, Thailand</p>
            </div>
          </div>

          {/* Bill To & Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 pb-8 border-b border-gray-100" data-id="element-8935">
            <div data-id="element-8936">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8937">
                Bill To
              </h3>
              <p className="text-sm font-semibold text-gray-900" data-id="element-8938">
                {invoice.customerName}
              </p>
              <p className="text-sm text-gray-600 mt-0.5" data-id="element-8939">
                {invoice.customerEmail}
              </p>
              {invoice.customerPhone && <p className="text-sm text-gray-600 mt-0.5" data-id="element-8940">
                  {invoice.customerPhone}
                </p>}
            </div>
            <div className="text-right space-y-2" data-id="element-8941">
              <div className="flex justify-end gap-4" data-id="element-8942">
                <span className="text-sm text-gray-500" data-id="element-8943">Issue Date</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-8944">
                  {new Date(invoice.issueDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
                </span>
              </div>
              <div className="flex justify-end gap-4" data-id="element-8945">
                <span className="text-sm text-gray-500" data-id="element-8946">Due Date</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-8947">
                  {new Date(invoice.dueDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
                </span>
              </div>
              {invoice.paidDate && <div className="flex justify-end gap-4" data-id="element-8948">
                  <span className="text-sm text-gray-500" data-id="element-8949">Paid Date</span>
                  <span className="text-sm font-medium text-green-600" data-id="element-8950">
                    {new Date(invoice.paidDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
                  </span>
                </div>}
            </div>
          </div>

          {/* Invoice Items */}
          <div className="mb-8" data-id="element-8951">
            <table className="w-full" data-id="element-8952">
              <thead data-id="element-8953">
                <tr className="border-b border-gray-200" data-id="element-8954">
                  <th className="py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8955">
                    Description
                  </th>
                  <th className="py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8956">
                    Qty
                  </th>
                  <th className="py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8957">
                    Unit Price
                  </th>
                  <th className="py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8958">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-8959">
                {invoice.items.map(item => <tr key={item.id} className="border-b border-gray-100" data-id="element-8960">
                    <td className="py-3.5 text-sm text-gray-900" data-id="element-8961">
                      {item.description}
                    </td>
                    <td className="py-3.5 text-sm text-gray-900 text-right" data-id="element-8962">
                      {item.quantity}
                    </td>
                    <td className="py-3.5 text-sm text-gray-900 text-right" data-id="element-8963">
                      THB {item.unitPrice.toLocaleString('en-US')}
                    </td>
                    <td className="py-3.5 text-sm font-medium text-gray-900 text-right" data-id="element-8964">
                      THB {item.total.toLocaleString('en-US')}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-8" data-id="element-8965">
            <div className="w-full max-w-xs space-y-2" data-id="element-8966">
              <div className="flex justify-between py-1.5" data-id="element-8967">
                <span className="text-sm text-gray-500" data-id="element-8968">Subtotal</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-8969">
                  THB {invoice.subtotal.toLocaleString('en-US')}
                </span>
              </div>
              {invoice.discountAmount && invoice.discountAmount > 0 && <div className="flex justify-between py-1.5" data-id="element-8970">
                  <span className="text-sm text-gray-500" data-id="element-8971">Discount</span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-8972">
                    - THB {invoice.discountAmount.toLocaleString('en-US')}
                  </span>
                </div>}
              {invoice.taxAmount && invoice.taxAmount > 0 && <div className="flex justify-between py-1.5" data-id="element-8973">
                  <span className="text-sm text-gray-500" data-id="element-8974">Tax</span>
                  <span className="text-sm font-medium text-gray-900" data-id="element-8975">
                    THB {invoice.taxAmount.toLocaleString('en-US')}
                  </span>
                </div>}
              <div className="flex justify-between py-2.5 border-t-2 border-gray-200" data-id="element-8976">
                <span className="text-base font-semibold text-gray-900" data-id="element-8977">
                  Total
                </span>
                <span className="text-base font-bold text-gray-900" data-id="element-8978">
                  THB {invoice.total.toLocaleString('en-US')}
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && <div className="pt-6 border-t border-gray-100" data-id="element-8979">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8980">
                Notes
              </h3>
              <p className="text-sm text-gray-600" data-id="element-8981">{invoice.notes}</p>
            </div>}
        </div>
      </div>

      {/* Action Cards */}
      {invoice.status === 'sent' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-8982">
          <div className="px-6 py-4 flex items-center justify-between" data-id="element-8983">
            <div data-id="element-8984">
              <h3 className="text-sm font-semibold text-gray-900" data-id="element-8985">
                Mark as Paid
              </h3>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-8986">
                Confirm that this invoice has been paid
              </p>
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors" data-id="element-8987">
              <CheckCircleIcon size={16} data-id="element-8988" />
              Mark as Paid
            </button>
          </div>
        </div>}

      {invoice.status !== 'cancelled' && invoice.status !== 'paid' && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-8989">
          <div className="px-6 py-4 flex items-center justify-between" data-id="element-8990">
            <div data-id="element-8991">
              <h3 className="text-sm font-semibold text-gray-900" data-id="element-8992">
                Cancel Invoice
              </h3>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-8993">
                This action cannot be undone
              </p>
            </div>
            <button className="flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium rounded-lg transition-colors" data-id="element-8994">
              <XCircleIcon size={16} data-id="element-8995" />
              Cancel Invoice
            </button>
          </div>
        </div>}
    </div>;
}